import React, { Dispatch, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ArrowBack from '@uber/base-icons/arrow-back';
import ChevronRight from '@uber/base-icons/chevron-right';
import { type Theme, styled } from 'baseui';
import { Button, KIND, SIZE } from 'baseui/button';
import { LabelLarge } from 'baseui/typography';
import _noop from 'lodash/noop';
import type { StyleObject } from 'styletron-react';
import type { FrameSpanT } from '../../image/labelling/reducers/actions';
import AnnotationGroupSettings from '../annotation-group/annotation-group-settings';
import { CollapsiblePanel } from '../collapsible-panel/collapsible-panel.react';
import { ShowHideButton } from '../collapsible-panel/trailing-toggle-component/show-hide-button.react';
import { MAX_LABEL_COUNT_ERROR_MESSAGE, NOTIFICATION_TYPE } from '../constant/constant';
import SidePopoverPanel from '../side-popover-panel/side-popover-panel';
import type { AnnotationLabelT, AnswerT, GenericAnnotationT, GetAnnotationMetaDetailsArgsT, GetAnnotationMetaDetailsT, LabellingValidationsT, OptionsT, TutorialServiceT } from '../types/types';
import type { ConfigT } from '../types/types';
import { type StoreTypeT, STORE_TYPE } from '../types/types';
import SideDrawer from '../utils/side-drawer';
import { checkLabelMaxReached, getParentAnnotationLabelTitle } from '../utils/utils';
import { isAnnotationHidden } from './utils/utils';
import AdditionalOptions from './additional-options';
import AnnotationsPane from './annotations-pane';
import LabelContainer from './label-container';
const DEFAULT_ADDITIONAL_OPTION_CONFIG = {
  invalidMedia: true,
  requiredLabel: true,
  noAnnotation: false
};
const Container = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${$theme.colors.borderOpaque}`,
  width: `calc(100% - ${$theme.sizing.scale100})`,
  height: '100%',
  overflow: 'auto',
  boxSizing: 'border-box'
}));
const PanelHeader = styled('div', {
  display: 'flex'
});
type PanelListItemT = {
  onPanelOpen?: () => void;
  onPanelClose?: () => void;
  state?: 'default' | 'error' | 'warning ';
  element: React.ReactNode;
};
export type SidePanelPropsT = {
  onFilterLabelUpdate?: (labels: {
    [key: string]: boolean;
  }) => void;
  annotationLabels: AnnotationLabelT[];
  updateAnnotation: (b: string, a: {
    newLabel?: AnnotationLabelT;
    newAnswers?: AnswerT;
  }) => void;
  updateDynamicAnnotation?: (annotationId: string, dynamicAnnotation: GenericAnnotationT, range: FrameSpanT) => void;
  labellingValidations?: LabellingValidationsT;
  editableAnnotationId: string | undefined | null;
  selectedAnnotationIds?: Array<string>;
  focusedAnnotationId: string | undefined | null;
  selectedAnnotationLabel: AnnotationLabelT | undefined | null;
  isAnnotationPanelOpen: boolean;
  onAnnotationDetailsPanelHide: () => void;
  onSelectAnnotation: (a: string) => void;
  onFocusAnnotation: (a: string) => void;
  onCopyAnnotationId?: (a: string | null) => void;
  onDeleteAnnotation: (a: string) => void;
  onAnnotationLockToggle?: (b: boolean, a: string, storeType?: StoreTypeT) => void;
  onAnnotationHideToggle: (b: boolean, a: string, storeType?: StoreTypeT) => void;
  onLockAnnotations?: (b: AnnotationLabelT, a: boolean, storeType?: StoreTypeT) => void;
  onHideAnnotations: (b: AnnotationLabelT, a: boolean, storeType?: StoreTypeT) => void;
  onShowHideAllAnnotations?: (hidden: boolean, options?: {
    storeType?: StoreTypeT;
    annotationLabels?: AnnotationLabelT[];
  }) => void;
  onSetAnnotationLabel: (a: AnnotationLabelT) => void;
  onUpdateValidation?: (labelingValidation: LabellingValidationsT) => void;
  showNotification: (notificationType: (typeof NOTIFICATION_TYPE)[keyof typeof NOTIFICATION_TYPE], message: string) => void;
  onAnnotationDetailsPanelOpen: () => void;
  shouldShowNestedLabels?: boolean;
  annotationList: GenericAnnotationT[];
  annotationMLList?: GenericAnnotationT[];
  annotationConsensusAnnotationsList?: GenericAnnotationT[];
  getAnnotationMetaDetails: GetAnnotationMetaDetailsT;
  showMLAnnotations?: boolean;
  showConsensusAnnotations?: boolean;
  annotationImportConfig?: {
    onImportAnnotations: (annotationIdList: string[]) => void;
    importedAnnotationIds: {
      [x: string]: boolean;
    };
  };
  options?: OptionsT;
  allAnnotationList?: GenericAnnotationT[];
  onDeleteAnnotations: (currentFrameOnly?: boolean, annotationLabels?: string[]) => void;
  tutorial?: TutorialServiceT;
  config?: ConfigT;
  showAdditionalOptions?: boolean;
  classificationQuestionnaire?: PanelListItemT;
  relationshipPanel?: PanelListItemT;
  allowedAnnotationLabelsMap?: {
    [annotationId: string]: AnnotationLabelT[];
  };
  additionOptionsVisibilityConfig?: {
    invalidMedia: boolean;
    requiredLabel: boolean;
    noAnnotation: boolean;
  };
  resetDynamicFields?: (annotationId: string, frameSpan: FrameSpanT) => void;
  getSelectedAnnotationFrameRange?: (annotationId: string) => {
    startFrameIndex: number;
    endFrameIndex: number;
  };
  getDynamicAnswersRange?: (a: string) => {
    frameStart: number;
    frameEnd: number;
    annotation: GenericAnnotationT;
    isValid: boolean;
    id: string;
    savedToStore: boolean;
  }[];
  isDynamicAttributeSupported?: boolean;
  getAnnotationDataDetail?: () => React.ReactNode;
  onValidateAnswers?: (annotationId: string, answers: any) => Promise<any[]>;
};
const PanelButtonContainer = styled('div', ({
  $theme
}) => ({
  paddingBottom: $theme.sizing.scale100,
  paddingTop: $theme.sizing.scale100,
  display: 'flex'
}));
const panelButtonOverrides = {
  BaseButton: {
    style: ({}: {
      $theme: Theme;
    }): StyleObject => ({
      flexGrow: 1,
      justifyContent: 'left',
      paddingLeft: '16px',
      paddingRight: '16px'
    })
  },
  EndEnhancer: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }): StyleObject => ({
      flexGrow: 1,
      justifyContent: 'end'
    })
  }
};
const PANEL_COUNT = 2;
const SidePanel = ({
  annotationLabels,
  updateAnnotation,
  labellingValidations,
  editableAnnotationId,
  selectedAnnotationIds,
  focusedAnnotationId,
  selectedAnnotationLabel,
  isAnnotationPanelOpen,
  classificationQuestionnaire,
  relationshipPanel,
  onSelectAnnotation,
  onFocusAnnotation,
  onCopyAnnotationId,
  onDeleteAnnotation,
  onAnnotationLockToggle,
  onAnnotationHideToggle,
  onLockAnnotations,
  onHideAnnotations,
  onShowHideAllAnnotations,
  onDeleteAnnotations,
  onSetAnnotationLabel,
  onUpdateValidation,
  showNotification,
  onAnnotationDetailsPanelHide,
  onAnnotationDetailsPanelOpen,
  shouldShowNestedLabels = false,
  annotationList,
  annotationMLList,
  annotationConsensusAnnotationsList,
  getAnnotationMetaDetails,
  showMLAnnotations,
  showConsensusAnnotations,
  annotationImportConfig,
  options,
  allAnnotationList,
  config,
  showAdditionalOptions = true,
  allowedAnnotationLabelsMap,
  additionOptionsVisibilityConfig = DEFAULT_ADDITIONAL_OPTION_CONFIG,
  updateDynamicAnnotation = _noop,
  getSelectedAnnotationFrameRange,
  getDynamicAnswersRange,
  resetDynamicFields = _noop,
  isDynamicAttributeSupported = false,
  getAnnotationDataDetail,
  onFilterLabelUpdate,
  onValidateAnswers
}: SidePanelPropsT) => {
  const [panelHeight, setPanelHeight] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isClassificationPanelOpen, setIsClassificationPanelOpen] = useState(false);
  const [isRelationshipPanelOpen, setIsRelationshipPanelOpen] = useState(false);
  const selectedAnnotation = useMemo(() => {
    return annotationList.find(annotation => annotation.id === editableAnnotationId);
  }, [annotationList, editableAnnotationId]);
  useEffect(() => {
    if (!panelRef.current) return;
    const resizeObserver = new ResizeObserver(() => {
      if (!panelRef.current) return;
      const {
        height
      } = panelRef.current.getBoundingClientRect();
      setPanelHeight(height / PANEL_COUNT);
    });
    resizeObserver.observe(panelRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  const handleEditAnnotation = useCallback((annotationId: string) => {
    onSelectAnnotation(annotationId);
    onAnnotationDetailsPanelOpen();
  }, [onAnnotationDetailsPanelOpen, onSelectAnnotation]);
  const handleCheckLabelMaxReached = useCallback((annotationLabel: AnnotationLabelT) => {
    const isMaxReached = checkLabelMaxReached(annotationLabel, annotationList);
    if (isMaxReached) {
      showNotification(NOTIFICATION_TYPE.ERROR, MAX_LABEL_COUNT_ERROR_MESSAGE(annotationLabel.title));
    }
    return isMaxReached;
  }, [annotationList, showNotification]);
  const showRequiredLabel = useMemo(() => annotationLabels.some(annotationLabel => annotationLabel.isRequired), [annotationLabels]);
  const handleHideMLAnnotations = useCallback((annotationLabel: AnnotationLabelT, hidden: boolean) => {
    onHideAnnotations(annotationLabel, hidden, STORE_TYPE.ML_ANNOTATION_STORE);
  }, [onHideAnnotations]);
  const handleMLAnnotationLockToggle = useCallback((hidden: boolean, annotationId: string) => {
    if (onAnnotationLockToggle) {
      onAnnotationLockToggle(hidden, annotationId, STORE_TYPE.ML_ANNOTATION_STORE);
    }
  }, [onAnnotationLockToggle]);
  const handleMLAnnotationHideToggle = useCallback((hidden: boolean, annotationId: string) => {
    onAnnotationHideToggle(hidden, annotationId, STORE_TYPE.ML_ANNOTATION_STORE);
  }, [onAnnotationHideToggle]);
  const handleHideConsensusAnnotations = useCallback((annotationLabel: AnnotationLabelT, hidden: boolean) => {
    onHideAnnotations(annotationLabel, hidden, STORE_TYPE.CONSENSUS_ANNOTATION_STORE);
  }, [onHideAnnotations]);
  const handleConsensusAnnotationHideToggle = useCallback((hidden: boolean, annotationId: string) => {
    onAnnotationHideToggle(hidden, annotationId, STORE_TYPE.CONSENSUS_ANNOTATION_STORE);
  }, [onAnnotationHideToggle]);
  const getAnnotationPanelTrailingComponent = useCallback((storeType?: StoreTypeT) => {
    const isHidden = isAnnotationHidden(annotationList, annotationMLList, storeType, annotationConsensusAnnotationsList);
    return <ShowHideButton hidden={isHidden} onClick={(hidden: boolean) => onShowHideAllAnnotations?.(hidden, {
      storeType
    })} />;
  }, [annotationList, annotationMLList, annotationConsensusAnnotationsList, onShowHideAllAnnotations]);
  const handleImportAnnotations = annotationsIdList => {
    // Show a toast or notification about import
    annotationImportConfig?.onImportAnnotations(annotationsIdList);
  };
  const [shouldDisplayAllAnnotations, setShouldDisplayAllAnnotations] = useState(false);
  const handleAllAnnotationsToggle = useCallback((value: boolean) => {
    setShouldDisplayAllAnnotations(!value);
  }, []);
  const annotationsToBeDisplayed = shouldDisplayAllAnnotations ? allAnnotationList : annotationList;
  let sortedAnnotationListByLabel = useMemo<GenericAnnotationT[]>(() => {
    return annotationLabels.reduce((sortedAnnotationList, label) => {
      const filteredAnnotationList = (annotationsToBeDisplayed || []).filter(annotation => getParentAnnotationLabelTitle(annotation.annotationLabel.title, annotationLabels) === label.title);
      // @ts-expect-error missing null check
      sortedAnnotationList.push(...filteredAnnotationList);
      return sortedAnnotationList;
    }, []);
  }, [annotationLabels, annotationsToBeDisplayed]);

  // adding shouldDisplayAllAnnotations to the function arguments to differentiate between
  // description for current frame annotations and all annotations.
  const updatedGetAnnotationMetaDetails = (metadataArgs: GetAnnotationMetaDetailsArgsT) => getAnnotationMetaDetails({
    ...metadataArgs,
    shouldDisplayAllAnnotations
  });

  // Displays "Annotation Group Settings" panel when at least one annotation is detected within
  // any of the frames for video labeling and LIDAR.
  const showAnnotationGroupSettings = options?.showAllAnnotations?.enabled ? allAnnotationList?.length !== 0 : annotationList.length !== 0;
  const showAllAnnotationsProps = {
    ...(options?.showAllAnnotations?.enabled && {
      isChecked: !shouldDisplayAllAnnotations,
      handleAllAnnotationsToggle
    })
  };
  const [labelMap, setLabelMap] = useState(() => annotationLabels.reduce((labelMap, annotationLabel) => {
    return {
      ...labelMap,
      [annotationLabel.title]: true
    };
  }, {}));
  const filteredAnnotationList = useMemo(() => sortedAnnotationListByLabel.filter(annotation => labelMap[getParentAnnotationLabelTitle(annotation.annotationLabel.title, annotationLabels)]), [labelMap, sortedAnnotationListByLabel, annotationLabels]);
  const handleDeleteAnnotations = (annotationLabel: string) => {
    const deleteCurrentFrameAnnotationsOnly = !shouldDisplayAllAnnotations;
    onDeleteAnnotations(deleteCurrentFrameAnnotationsOnly, [annotationLabel]);
  };
  const handleGroupShowHideAllAnnotations = (hidden: boolean) => {
    onShowHideAllAnnotations?.(hidden, {
      annotationLabels: annotationLabels.filter(label => labelMap[label.title])
    });
  };
  const handleGroupAnnotationDeletion = (currentFrameOnly: boolean) => {
    const deletedAnnotationLabels = annotationLabels.filter(label => labelMap[label.title]).map(label => label.title);
    onDeleteAnnotations(currentFrameOnly, deletedAnnotationLabels);
  };
  const handleLableFilter = useCallback((labels: {
    [key: string]: boolean;
  }) => {
    setLabelMap(labels);
    onFilterLabelUpdate?.(labels);
  }, [onFilterLabelUpdate, setLabelMap]);
  return <>
      <Container ref={panelRef}>
        <CollapsiblePanel title={'Labels'}>
          <LabelContainer height={panelHeight} annotationLabels={annotationLabels} onSetAnnotationLabel={onSetAnnotationLabel} selectedAnnotationLabel={selectedAnnotationLabel} isSegmentation={shouldShowNestedLabels} annotationList={annotationList} showNotification={showNotification} />
        </CollapsiblePanel>
        <CollapsiblePanel title={'Annotations'}>
          {showAnnotationGroupSettings && <AnnotationGroupSettings isHidden={isAnnotationHidden(filteredAnnotationList)} count={filteredAnnotationList.length} onShowHideAllAnnotations={handleGroupShowHideAllAnnotations} setLabelMap={handleLableFilter} labelMap={labelMap} handleDeleteAllAnnotations={handleGroupAnnotationDeletion} {...showAllAnnotationsProps} />}
          <AnnotationsPane
        // @ts-expect-error todo(ts-migration) TS2322 Type '{ updateAnnotation: (b: string, a: { newLabel?: AnnotationLabelT; newAnswers?: AnswerT; }) => void; annotationLabels: AnnotationLabelT[]; ... 9 more ...; annotationImportConfig: { ...; }; }' is ...
        updateAnnotation={updateAnnotation} annotationLabels={annotationLabels} editableAnnotationId={editableAnnotationId} selectedAnnotationIds={selectedAnnotationIds || []} focusedAnnotationId={focusedAnnotationId} panelHeight={panelHeight} validations={labellingValidations} hideAnnotations={onHideAnnotations} handleSelectAnnotation={onSelectAnnotation} handleFocusAnnotation={onFocusAnnotation} handleAnnotationEdit={handleEditAnnotation} handleCopyAnnotationId={onCopyAnnotationId} handleDeleteAnnotation={onDeleteAnnotation} handleAnnotationHideToggle={onAnnotationHideToggle} annotationList={filteredAnnotationList} getAnnotationMetaDetails={updatedGetAnnotationMetaDetails} handleDeleteAnnotations={handleDeleteAnnotations} {...onLockAnnotations && onAnnotationLockToggle && {
          lockAnnotations: onLockAnnotations,
          handleAnnotationLockToggle: onAnnotationLockToggle
        }} />
        </CollapsiblePanel>
        {annotationMLList && <CollapsiblePanel title={'ML Annotations'} setInitiallyExpanded={showMLAnnotations} trailingComponent={getAnnotationPanelTrailingComponent(STORE_TYPE.ML_ANNOTATION_STORE)}>
            <AnnotationsPane
        // @ts-expect-error todo(ts-migration) TS2322 Type '{ updateAnnotation: (b: string, a: { newLabel?: AnnotationLabelT; newAnswers?: AnswerT; }) => void; annotationLabels: AnnotationLabelT[]; ... 9 more ...; annotationImportConfig: { ...; }; }' is ...
        updateAnnotation={updateAnnotation} annotationLabels={annotationLabels} editableAnnotationId={editableAnnotationId} selectedAnnotationIds={selectedAnnotationIds || []} focusedAnnotationId={focusedAnnotationId} panelHeight={panelHeight} validations={labellingValidations} hideAnnotations={handleHideMLAnnotations} handleSelectAnnotation={onSelectAnnotation} handleFocusAnnotation={onFocusAnnotation} handleCopyAnnotationId={onCopyAnnotationId} handleAnnotationHideToggle={handleMLAnnotationHideToggle} showNotification={showNotification} annotationList={annotationMLList} getAnnotationMetaDetails={getAnnotationMetaDetails} annotationImportConfig={annotationImportConfig && {
          onImportAnnotations: handleImportAnnotations,
          importedAnnotationIds: annotationImportConfig.importedAnnotationIds
        }} {...onAnnotationLockToggle && {
          lockAnnotations: onLockAnnotations,
          handleAnnotationLockToggle: handleMLAnnotationLockToggle
        }} />
          </CollapsiblePanel>}
        {annotationConsensusAnnotationsList && <CollapsiblePanel title={'Consensus Annotations'} setInitiallyExpanded={showConsensusAnnotations} trailingComponent={getAnnotationPanelTrailingComponent(STORE_TYPE.CONSENSUS_ANNOTATION_STORE)}>
            <AnnotationsPane
        // @ts-expect-error todo(ts-migration) TS2322 Type '{ updateAnnotation: (b: string, a: { newLabel?: AnnotationLabelT; newAnswers?: AnswerT; }) => void; annotationLabels: AnnotationLabelT[]; ... 9 more ...; annotationImportConfig: { ...; }; }' is ...
        updateAnnotation={updateAnnotation} annotationLabels={annotationLabels} editableAnnotationId={editableAnnotationId} selectedAnnotationIds={selectedAnnotationIds || []} focusedAnnotationId={focusedAnnotationId} panelHeight={panelHeight} validations={labellingValidations} hideAnnotations={handleHideConsensusAnnotations} handleSelectAnnotation={onSelectAnnotation} handleFocusAnnotation={onFocusAnnotation} handleCopyAnnotationId={onCopyAnnotationId} handleAnnotationHideToggle={handleConsensusAnnotationHideToggle} showNotification={showNotification} annotationList={annotationConsensusAnnotationsList} getAnnotationMetaDetails={getAnnotationMetaDetails} annotationImportConfig={annotationImportConfig} isConsensusPanel={true} />
          </CollapsiblePanel>}
        {showAdditionalOptions && onUpdateValidation && labellingValidations && <CollapsiblePanel title={'Additional options'}>
            <AdditionalOptions showRequiredLabel={showRequiredLabel} updateValidation={onUpdateValidation} validations={labellingValidations} additionOptionsVisibilityConfig={additionOptionsVisibilityConfig} />
          </CollapsiblePanel>}
        {classificationQuestionnaire !== undefined ? <PanelButtonContainer>
            <Button onClick={() => {
          classificationQuestionnaire?.onPanelOpen?.();
          setIsClassificationPanelOpen(true);
        }} size={SIZE.default} kind={KIND.tertiary} overrides={panelButtonOverrides} endEnhancer={<ChevronRight />}>
              Classification
            </Button>
          </PanelButtonContainer> : undefined}
        {relationshipPanel !== undefined ? <PanelButtonContainer>
            <Button onClick={() => {
          relationshipPanel?.onPanelOpen?.();
          setIsRelationshipPanelOpen(true);
        }} size={SIZE.default} kind={KIND.tertiary} overrides={panelButtonOverrides} endEnhancer={<ChevronRight />}>
              Relationship
            </Button>
          </PanelButtonContainer> : undefined}
      </Container>
      <SideDrawer isOpen={isClassificationPanelOpen}>
        <PanelHeader>
          <Button onClick={() => {
          setIsClassificationPanelOpen(false);
        }} size={SIZE.compact} kind={KIND.tertiary}>
            <ArrowBack />
          </Button>
          <LabelLarge style={{
          alignContent: 'center'
        }}>Classification</LabelLarge>
        </PanelHeader>
        {classificationQuestionnaire?.element}
      </SideDrawer>
      <SideDrawer isOpen={isRelationshipPanelOpen}>
        <PanelHeader>
          <Button onClick={() => {
          relationshipPanel?.onPanelClose?.();
          setIsRelationshipPanelOpen(false);
        }} size={SIZE.compact} kind={KIND.tertiary}>
            <ArrowBack title={'Close relationship panel'} />
          </Button>
          <LabelLarge style={{
          alignContent: 'center'
        }}>Relationship</LabelLarge>
        </PanelHeader>
        {relationshipPanel?.element}
      </SideDrawer>
      {editableAnnotationId && selectedAnnotation && <SideDrawer isOpen={isAnnotationPanelOpen}>
          <SidePopoverPanel selectedAnnotation={selectedAnnotation} annotationLabels={annotationLabels} editableAnnotationId={editableAnnotationId} onCancel={() => {
        onAnnotationDetailsPanelHide();
      }} isQuestionnaireComposable={config?.useComposableQuestionnaire} onUpdateAnnotation={(annotationId, annotationUpdates) => {
        updateAnnotation(annotationId, annotationUpdates);
        onAnnotationDetailsPanelHide();
      }} updateDynamicAnnotation={updateDynamicAnnotation} showNotification={showNotification} checkLabelMaxReached={handleCheckLabelMaxReached} getAnnotationMetaDetails={getAnnotationMetaDetails} allowedAnnotationLabels={allowedAnnotationLabelsMap?.[selectedAnnotation.id]} getSelectedAnnotationFrameRange={getSelectedAnnotationFrameRange} getDynamicAnswersRange={getDynamicAnswersRange} resetDynamicFields={resetDynamicFields} isDynamicAttributeSupported={isDynamicAttributeSupported} getAnnotationDataDetail={getAnnotationDataDetail} onValidateAnswers={onValidateAnswers} />
        </SideDrawer>}
    </>;
};
export default SidePanel;